/**
 * admin_business_members_controller.js — Admin > business detail > Members (Stimulus).
 *
 * Adds a user to the business (search, pick, choose a role), changes a
 * member's role and removes a member, through the JSON routes under
 * /admin/api/businesses/<id>/members. A change toasts and re-renders the page
 * in place through window.chippiePageCheck.refresh(), which waits for the
 * closing modal to finish before it swaps.
 *
 * Values: url (the business's /admin/api/businesses/<id> base).
 * Targets: the add modal (addModal, search, results, selected, selectedName,
 * role, addButton) and the change-role modal (roleModal, roleMemberName,
 * roleSelect, saveRoleButton).
 *
 * Template: templates/admin/business_detail.html
 */

import { Controller } from "@hotwired/stimulus";

const SEARCH_DELAY_MS = 300;

export default class extends Controller {
    static values = { url: String };
    static targets = [
        'addModal', 'search', 'results', 'selected', 'selectedName', 'role', 'addButton',
        'roleModal', 'roleMemberName', 'roleSelect', 'saveRoleButton'
    ];

    disconnect() {
        clearTimeout(this._searchTimer);
    }

    openAdd() {
        this.searchTarget.value = '';
        this._resultsMessage('Type to search for users...', 'text-muted');
        this.selectedTarget.classList.add('d-none');
        this._selectedUserId = null;
        this.addButtonTarget.disabled = true;
        this.roleTarget.value = 'member';
        bootstrap.Modal.getOrCreateInstance(this.addModalTarget).show();
    }

    search() {
        clearTimeout(this._searchTimer);
        const query = this.searchTarget.value.trim();
        if (query.length < 2) {
            this._resultsMessage('Type at least 2 characters to search...', 'text-muted');
            return;
        }
        this._searchTimer = setTimeout(() => this._runSearch(query), SEARCH_DELAY_MS);
    }

    select(event) {
        event.preventDefault();
        const { id, name, email } = event.params;
        this._selectedUserId = id;
        this.selectedNameTarget.textContent = `${name} (${email})`;
        this.selectedTarget.classList.remove('d-none');
        this.addButtonTarget.disabled = false;
        this.searchTarget.value = '';
        this._resultsMessage('User selected. Search again to change.', 'text-muted');
    }

    add() {
        if (!this._selectedUserId) return;
        const done = this._busyButton(this.addButtonTarget, 'Adding...');
        this._send('/members', 'POST', { user_id: this._selectedUserId, role: this.roleTarget.value }, 'Member added')
            .then((ok) => {
                if (ok) bootstrap.Modal.getInstance(this.addModalTarget).hide();
                done();
            });
    }

    changeRole(event) {
        const { id, name, role } = event.params;
        this._roleMemberId = id;
        this.roleMemberNameTarget.textContent = name;
        this.roleSelectTarget.value = role;
        bootstrap.Modal.getOrCreateInstance(this.roleModalTarget).show();
    }

    saveRole() {
        const done = this._busyButton(this.saveRoleButtonTarget, 'Saving...');
        this._send(`/members/${this._roleMemberId}/role`, 'PUT', { role: this.roleSelectTarget.value }, 'Role changed')
            .then((ok) => {
                if (ok) bootstrap.Modal.getInstance(this.roleModalTarget).hide();
                done();
            });
    }

    remove(event) {
        const { id, name } = event.params;
        if (!confirm(`Remove ${name} from this business?`)) return;
        this._send(`/members/${id}`, 'DELETE', null, `Removed ${name}`);
    }

    _runSearch(query) {
        fetch(`${this.urlValue}/search-users?q=${encodeURIComponent(query)}`, { credentials: 'same-origin' })
            .then(function (response) { return response.json(); })
            .then((data) => {
                if (!data.success) throw new Error(data.message);
                if (data.users.length === 0) {
                    this._resultsMessage('No users found', 'text-muted');
                    return;
                }
                this.resultsTarget.replaceChildren(...data.users.map(userResult));
            })
            .catch(() => this._resultsMessage('Error searching users', 'text-danger'));
    }

    /** Resolves true when the server accepted the change. */
    _send(path, method, body, success) {
        const options = { method: method, credentials: 'same-origin' };
        if (body) {
            options.headers = { 'Content-Type': 'application/json' };
            options.body = JSON.stringify(body);
        }
        return fetch(this.urlValue + path, options)
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (!data.success) throw new Error(data.message || 'Unknown error');
                window.showNotification(success, 'success');
                window.chippiePageCheck.refresh();
                return true;
            })
            .catch(function (error) {
                window.showNotification('Failed: ' + error.message, 'error');
                return false;
            });
    }

    _resultsMessage(text, tone) {
        const item = document.createElement('div');
        item.className = 'list-group-item ' + tone;
        item.textContent = text;
        this.resultsTarget.replaceChildren(item);
    }

    /** Disable a button with a progress label; the returned function restores it. */
    _busyButton(button, label) {
        const original = button.innerHTML;
        button.disabled = true;
        button.textContent = label;
        return function () {
            button.disabled = false;
            button.innerHTML = original;
        };
    }
}

/** One search hit, wired to this controller's select action by params. */
function userResult(user) {
    const item = document.createElement('a');
    item.href = '#';
    item.className = 'list-group-item list-group-item-action';
    item.dataset.action = 'admin-business-members#select';
    item.dataset.adminBusinessMembersIdParam = user.id;
    item.dataset.adminBusinessMembersNameParam = user.display_name;
    item.dataset.adminBusinessMembersEmailParam = user.email;

    const wrapper = document.createElement('div');
    wrapper.className = 'd-flex justify-content-between align-items-center';
    const info = document.createElement('div');
    const name = document.createElement('strong');
    name.textContent = user.display_name;
    const email = document.createElement('div');
    email.className = 'small text-muted';
    email.textContent = user.email;
    info.append(name, email);
    const icon = document.createElement('i');
    icon.className = 'bi bi-plus-circle text-primary';
    wrapper.append(info, icon);
    item.appendChild(wrapper);
    return item;
}
